import {
    CHECK_IN_CATEGORIES,
    CHECK_IN_CATEGORY,
    mapGooglePlaceTypesToCheckInCategory
} from "./checkIn";
import { loadGoogleMapsLibraries } from "./googleMaps";

const SEARCH_RESULT_LIMIT = 10;
const NEARBY_RESULT_LIMIT = 10;

const PLACE_FIELDS = [
    "id",
    "displayName",
    "formattedAddress",
    "location",
    "types",
    "photos"
];

const toLatLng = coordinates => {
    const lat = Number(coordinates?.latitude);
    const lng = Number(coordinates?.longitude);

    return Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : null;
};

const getLocation = location => {
    if (!location) {
        return {};
    }

    return {
        latitude: typeof location.lat === "function"
            ? location.lat()
            : location.lat,
        longitude: typeof location.lng === "function"
            ? location.lng()
            : location.lng
    };
};

const getPhotoUrl = place => {
    const photo = place?.photos?.[0];

    if (!photo) {
        return "";
    }

    try {
        if (typeof photo.getURI === "function") {
            return photo.getURI({
                maxWidth: 600,
                maxHeight: 400
            });
        }

        if (typeof photo.getUrl === "function") {
            return photo.getUrl({
                maxWidth: 600,
                maxHeight: 400
            });
        }
    } catch {
        return "";
    }

    return "";
};

const formatPlace = place => {
    if (!place?.id) {
        return null;
    }

    const providerPlaceTypes = Array.isArray(place.types)
        ? place.types
        : [];

    const { latitude, longitude } = getLocation(place.location);

    return {
        placeId: place.id,
        placeName: place.displayName || "",
        formattedAddress: place.formattedAddress || "",
        latitude,
        longitude,
        provider: "google",
        providerPlaceTypes,
        placeImageReference: getPhotoUrl(place),
        category: mapGooglePlaceTypesToCheckInCategory(providerPlaceTypes)
    };
};

const categorySortScore = (place, category) => {
    const configuredCategory = CHECK_IN_CATEGORIES[category];

    if (!configuredCategory) {
        return 0;
    }

    const placeTypes = new Set(place.providerPlaceTypes || []);

    return configuredCategory.googlePlaceTypes?.some(type =>
        placeTypes.has(type)
    ) ? -1 : 0;
};

const formatPlaces = (places, category) =>
    places
        .map(formatPlace)
        .filter(place => place?.placeId && place?.placeName)
        .sort((a, b) =>
            categorySortScore(a, category) -
            categorySortScore(b, category)
        )
        .slice(0, SEARCH_RESULT_LIMIT);

const getPlacesLibrary = async () => {
    const { Place } = await window.google.maps.importLibrary("places");
    return Place;
};

const mergePlaces = placesLists => {
    const placeMap = new Map();

    placesLists.flat().forEach(place => {
        if (place?.id && !placeMap.has(place.id)) {
            placeMap.set(place.id, place);
        }
    });

    return Array.from(placeMap.values());
};

const searchNearbyPlaces = async ({ coordinates, category }) => {
    const location = toLatLng(coordinates);

    if (!location) {
        return [];
    }

    const configuredCategory =
        CHECK_IN_CATEGORIES[category] || CHECK_IN_CATEGORIES.location;

    const includedTypes = configuredCategory.nearbySearchTypes || [];

    if (!includedTypes.length) {
        return [];
    }

    try {
        const Place = await getPlacesLibrary();

        const { places = [] } = await Place.searchNearby({
            fields: PLACE_FIELDS,
            locationRestriction: {
                center: location,
                radius: 50000
            },
            includedTypes,
            maxResultCount: NEARBY_RESULT_LIMIT
        });

        return formatPlaces(places, category);
    } catch (error) {
        console.warn("Google Nearby Search failed:", error);
        return [];
    }
};

export const searchGoogleCheckInPlaces = async ({
    query,
    coordinates,
    category
}) => {
    await loadGoogleMapsLibraries();

    const trimmedQuery = query?.trim() || "";

    if (!trimmedQuery) {
        return getNearbyGoogleCheckInPlaces({
            coordinates,
            category
        });
    }

    try {
        const Place = await getPlacesLibrary();
        const configuredCategory = CHECK_IN_CATEGORIES[category];
        const searchTextQueries = [trimmedQuery];

        if (configuredCategory?.id === CHECK_IN_CATEGORY.LOCATION) {
            searchTextQueries.unshift(
                `points of interest in ${trimmedQuery}`,
                `places in ${trimmedQuery}`
            );
        } else if (configuredCategory) {
            searchTextQueries.unshift(`${trimmedQuery} ${configuredCategory.label}`);
        }

        const placeResponses = await Promise.all(
            searchTextQueries.map(textQuery =>
                Place.searchByText({
                    textQuery,
                    fields: PLACE_FIELDS,
                    maxResultCount: SEARCH_RESULT_LIMIT
                })
            )
        );

        const places = mergePlaces(
            placeResponses.map(response => response.places || [])
        );

        return formatPlaces(places, category);
    } catch (error) {
        console.error("Google Text Search failed:", error);
        return [];
    }
};

export const getNearbyGoogleCheckInPlaces = async ({
    coordinates,
    category
}) => {
    await loadGoogleMapsLibraries();

    return searchNearbyPlaces({
        coordinates,
        category
    });
};

export const getGoogleCheckInPlace = async placeId => {
    if (!placeId) {
        return null;
    }

    await loadGoogleMapsLibraries();

    try {
        const Place = await getPlacesLibrary();
        const place = new Place({ id: placeId });

        await place.fetchFields({
            fields: PLACE_FIELDS
        });

        return formatPlace(place);
    } catch (error) {
        console.warn("Google Place lookup failed:", error);
        return null;
    }
};
